import { useMemo } from 'react';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import {
	useDisableVideoCreation,
	useSupporterVoicesR2Arm,
	useTrackVideoAskView,
	useUserRecordingCapability,
} from '#src/app/pages/actions/shared/hooks';
import type { SupporterVoicesR2CopyArm } from '#src/app/pages/actions/shared/hooks';
import type { UserRecordingCapability } from '#src/app/pages/actions/shared/types';
import { useCurrentUser } from '#src/app/shared/hooks/session';

import { getLocationLabel } from '../../../VideoFirstAsk/hooks/useVideoFirstAsk/getLocationLabel';

type Result = ModelHookResult<{
	capability: UserRecordingCapability;
	disableVideoCreation: boolean;
	/** Undefined for control, or an R2 viewer this experiment isn't relevant to (CHANGE-99359). */
	r2CopyArm: SupporterVoicesR2CopyArm | undefined;
	/**
	 * Handed over rather than interpolated here so the headline's i18n keys stay literal where they
	 * are rendered, and `no-invalid-translation-keys` can still check them.
	 */
	username: string | undefined;
	location: string | undefined;
}>;

export function useAddYourVideoInvite(): Result {
	const currentUser = useCurrentUser();
	const capability = useUserRecordingCapability();
	const disableVideoCreation = useDisableVideoCreation();
	const r2Arm = useSupporterVoicesR2Arm();
	const {
		data: {
			petition: { relevantLocationLocalizedName },
		},
	} = usePetitionActionsPageData();
	useTrackVideoAskView();

	const username = currentUser?.shortDisplayName ?? currentUser?.displayName;
	const location = useMemo(() => getLocationLabel(relevantLocationLocalizedName), [relevantLocationLocalizedName]);

	const r2CopyArm = r2Arm === 'v3_static_faces' ? undefined : r2Arm;

	return {
		data: {
			capability,
			disableVideoCreation,
			r2CopyArm,
			username,
			location,
		},
	};
}
